import { PythonTopicExercise } from '../../../../types/python-topic';

export const complejidadOrganizacionExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Búsqueda binaria',
    description: 'Implementa búsqueda binaria, un algoritmo eficiente para buscar elementos en listas ordenadas.',
    initialCode: `# Implementa búsqueda binaria
def busqueda_binaria(lista, objetivo):
    # Lista debe estar ordenada
    izquierda = 0
    derecha = len(lista) - 1
    
    while izquierda <= derecha:
        medio = 
        if lista[medio] == objetivo:
            return medio
        elif lista[medio] < objetivo:
            izquierda = 
        else:
            derecha = 
    return -1

# Prueba la función
numeros = [1, 3, 5, 7, 9, 11, 13, 15]
resultado = busqueda_binaria(numeros, 7)
print(f"Índice de 7: {resultado}")`,
    solution: `# Implementa búsqueda binaria
def busqueda_binaria(lista, objetivo):
    # Lista debe estar ordenada
    izquierda = 0
    derecha = len(lista) - 1
    
    while izquierda <= derecha:
        medio = (izquierda + derecha) // 2
        if lista[medio] == objetivo:
            return medio
        elif lista[medio] < objetivo:
            izquierda = medio + 1
        else:
            derecha = medio - 1
    return -1

# Prueba la función
numeros = [1, 3, 5, 7, 9, 11, 13, 15]
resultado = busqueda_binaria(numeros, 7)
print(f"Índice de 7: {resultado}")`,
    testCases: [],
    hints: [
      'El medio es (izquierda + derecha) // 2',
      'Si el elemento en medio es menor, busca a la derecha',
      'Si el elemento en medio es mayor, busca a la izquierda',
      'La lista debe estar ordenada para que funcione'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué búsqueda binaria?',
      content: `La **búsqueda binaria** es mucho más eficiente que búsqueda lineal para listas ordenadas. Reduce el tiempo de búsqueda significativamente.

### ¿Por qué este ejercicio?
- **Eficiencia**: O(log n) vs O(n) de búsqueda lineal
- **Aplicación práctica**: Muy común en algoritmos
- **Fundamentos**: Base para algoritmos más complejos
- **Optimización**: Esencial para listas grandes

### En el mundo real:
Cuando un sistema busca un usuario en una base de datos ordenada por ID, usa búsqueda binaria. Con millones de usuarios, búsqueda lineal sería extremadamente lenta, pero búsqueda binaria encuentra el resultado en pocos pasos.`,
      keyPoints: [
        'Búsqueda binaria requiere lista ordenada',
        'Divide el espacio de búsqueda a la mitad en cada paso',
        'Complejidad O(log n) vs O(n) de búsqueda lineal',
        'Muy eficiente para listas grandes'
      ],
      realWorldExample: 'En un sistema de diccionario, buscar una palabra en una lista ordenada alfabéticamente usa búsqueda binaria para encontrar rápidamente.'
    },
    theory: `# Búsqueda Binaria

## ¿Qué es Búsqueda Binaria?

La **búsqueda binaria** es un algoritmo que encuentra un elemento en una lista **ordenada** dividiendo repetidamente el espacio de búsqueda a la mitad.

## Cómo Funciona

1. Compara el elemento objetivo con el elemento del medio
2. Si son iguales, retorna el índice
3. Si el objetivo es menor, busca en la mitad izquierda
4. Si el objetivo es mayor, busca en la mitad derecha
5. Repite hasta encontrar o agotar el espacio

## Implementación

\`\`\`python
def busqueda_binaria(lista, objetivo):
    izquierda = 0
    derecha = len(lista) - 1
    
    while izquierda <= derecha:
        medio = (izquierda + derecha) // 2
        if lista[medio] == objetivo:
            return medio
        elif lista[medio] < objetivo:
            izquierda = medio + 1
        else:
            derecha = medio - 1
    return -1
\`\`\`

## Complejidad

- **Tiempo**: O(log n) - muy eficiente
- **Espacio**: O(1) - no usa espacio extra
- **Requisito**: Lista debe estar ordenada

## Ventajas

- **Muy eficiente**: Encuentra elementos rápidamente
- **Escalable**: Funciona bien con listas grandes
- **Fundamental**: Base para muchos algoritmos`,
    tips: [
      'La lista debe estar ordenada',
      'Calcula el medio con (izquierda + derecha) // 2',
      'Ajusta izquierda o derecha según la comparación',
      'El bucle continúa mientras izquierda <= derecha'
    ],
    advice: [
      'Asegúrate de que la lista esté ordenada',
      'Practica con diferentes casos (encontrado, no encontrado)',
      'Entiende por qué es O(log n)',
      'Esta es una técnica fundamental en algoritmos'
    ]
  },
  {
    id: 2,
    title: 'Crear y ejecutar un script simple',
    description: 'Aprende a crear un script Python simple que se puede ejecutar desde la línea de comandos.',
    initialCode: `# Script simple: calcula el área de un círculo
# Define una función para calcular el área
def area_circulo(radio):
    # Completa: área = π * radio²
    # Usa: 3.14159 como aproximación de π
    pass

# Ejecuta el script
radio = 5
area = area_circulo(radio)
print(f"Área del círculo con radio {radio}: {area}")`,
    solution: `# Script simple: calcula el área de un círculo
# Define una función para calcular el área
def area_circulo(radio):
    # Completa: área = π * radio²
    # Usa: 3.14159 como aproximación de π
    return 3.14159 * radio ** 2

# Ejecuta el script
radio = 5
area = area_circulo(radio)
print(f"Área del círculo con radio {radio}: {area}")`,
    testCases: [],
    hints: [
      'Un script es un archivo Python que se ejecuta directamente',
      'Puedes definir funciones y ejecutar código',
      'π ≈ 3.14159',
      'Área = π * radio²'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué crear scripts?',
      content: `Los **scripts** son archivos Python ejecutables que automatizan tareas. Son la base para programas más complejos.

### ¿Por qué este ejercicio?
- **Automatización**: Ejecutar tareas repetitivas
- **Fundamentos**: Base para programas más complejos
- **Aplicación práctica**: Muy común en desarrollo
- **Simplicidad**: Empezar con scripts simples

### En el mundo real:
Cuando un sistema necesita calcular áreas de círculos automáticamente, crea un script que se ejecuta con diferentes valores. Sin scripts, tendrías que calcular manualmente cada vez.`,
      keyPoints: [
        'Un script es un archivo Python ejecutable',
        'Puedes definir funciones y ejecutar código',
        'Se ejecuta desde la línea de comandos',
        'Base para programas más complejos'
      ],
      realWorldExample: 'En un sistema de cálculos, un script calcula áreas de diferentes formas geométricas automáticamente.'
    },
    theory: `# Crear y Ejecutar Scripts

## ¿Qué es un Script?

Un **script** es un archivo Python que contiene código ejecutable.

## Estructura Básica

\`\`\`python
# Definir funciones
def mi_funcion():
    pass

# Ejecutar código
resultado = mi_funcion()
print(resultado)
\`\`\`

## Ejecutar Scripts

Desde la línea de comandos:
\`\`\`bash
python script.py
\`\`\`

## Ventajas

- **Automatización**: Ejecutar tareas automáticamente
- **Reutilización**: Ejecutar múltiples veces
- **Organización**: Código estructurado`,
    tips: [
      'Un script es un archivo Python ejecutable',
      'Puedes definir funciones y ejecutar código',
      'Se ejecuta desde la línea de comandos',
      'Base para programas más complejos',
      'Puedes pasar argumentos desde la línea de comandos'
    ],
    advice: [
      'Practica creando scripts simples',
      'Experimenta ejecutando scripts',
      'Aprende a estructurar código en scripts',
      'Practica en contextos de automatización'
    ]
  },
  {
    id: 3,
    title: 'Organizar código en funciones',
    description: 'Aprende a organizar código en funciones reutilizables para mejorar la estructura del programa.',
    initialCode: `# Organiza este código en funciones
# Crea una función calcular_promedio(lista) que calcule el promedio
# Crea una función encontrar_maximo(lista) que encuentre el máximo

numeros = [10, 20, 30, 40, 50]

# Completa las funciones
def calcular_promedio(lista):
    pass

def encontrar_maximo(lista):
    pass

# Usa las funciones
promedio = calcular_promedio(numeros)
maximo = encontrar_maximo(numeros)
print(f"Promedio: {promedio}, Máximo: {maximo}")`,
    solution: `# Organiza este código en funciones
# Crea una función calcular_promedio(lista) que calcule el promedio
# Crea una función encontrar_maximo(lista) que encuentre el máximo

numeros = [10, 20, 30, 40, 50]

# Completa las funciones
def calcular_promedio(lista):
    return sum(lista) / len(lista)

def encontrar_maximo(lista):
    return max(lista)

# Usa las funciones
promedio = calcular_promedio(numeros)
maximo = encontrar_maximo(numeros)
print(f"Promedio: {promedio}, Máximo: {maximo}")`,
    testCases: [],
    hints: [
      'Organiza código repetitivo en funciones',
      'sum(lista) / len(lista) calcula el promedio',
      'max(lista) encuentra el máximo',
      'Las funciones hacen el código más reutilizable'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué organizar en funciones?',
      content: `Organizar código en **funciones** mejora la legibilidad, reutilización y mantenibilidad. Es fundamental para programas bien estructurados.

### ¿Por qué este ejercicio?
- **Reutilización**: Usar código múltiples veces
- **Legibilidad**: Código más claro y organizado
- **Mantenibilidad**: Fácil modificar y actualizar
- **Fundamentos**: Base para programas complejos

### En el mundo real:
Cuando un sistema necesita calcular promedios en múltiples lugares, crea una función reutilizable. Sin funciones, tendrías que repetir código en cada lugar.`,
      keyPoints: [
        'Las funciones organizan código reutilizable',
        'Mejoran la legibilidad y mantenibilidad',
        'Evitan repetición de código',
        'Base para programas bien estructurados'
      ],
      realWorldExample: 'En un sistema de análisis, funciones como calcular_promedio() se usan en múltiples reportes.'
    },
    theory: `# Organizar Código en Funciones

## ¿Por qué Funciones?

Las **funciones** organizan código en bloques reutilizables.

## Ventajas

- **Reutilización**: Usar código múltiples veces
- **Legibilidad**: Código más claro
- **Mantenibilidad**: Fácil modificar
- **Organización**: Estructura clara

## Estructura

\`\`\`python
def calcular_promedio(lista):
    return sum(lista) / len(lista)

resultado = calcular_promedio([10, 20, 30])
\`\`\`

## Mejores Prácticas

- **Nombres descriptivos**: calcular_promedio, no calc
- **Una responsabilidad**: Cada función hace una cosa
- **Reutilización**: Evita código duplicado`,
    tips: [
      'Organiza código repetitivo en funciones',
      'Usa nombres descriptivos',
      'Cada función debe tener una responsabilidad',
      'Las funciones mejoran la reutilización',
      'Base para programas bien estructurados'
    ],
    advice: [
      'Practica identificando código que se repite',
      'Experimenta organizando código en funciones',
      'Aprende a nombrar funciones descriptivamente',
      'Practica en contextos de organización de código'
    ]
  },
  {
    id: 4,
    title: 'Usar argumentos por defecto',
    description: 'Aprende a definir funciones con argumentos por defecto para mayor flexibilidad.',
    initialCode: `# Crea una función saludar con un argumento por defecto
# nombre por defecto debe ser "Usuario"
def saludar(nombre=):
    return f"Hola, {nombre}!"

# Prueba la función con y sin argumento
print(saludar("Ana"))
print(saludar())`,
    solution: `# Crea una función saludar con un argumento por defecto
# nombre por defecto debe ser "Usuario"
def saludar(nombre="Usuario"):
    return f"Hola, {nombre}!"

# Prueba la función con y sin argumento
print(saludar("Ana"))
print(saludar())`,
    testCases: [],
    hints: [
      'Argumentos por defecto se definen con =',
      'nombre="Usuario" establece un valor por defecto',
      'Puedes llamar la función con o sin argumento',
      'Útil para funciones flexibles'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué argumentos por defecto?',
      content: `Los **argumentos por defecto** hacen las funciones más flexibles, permitiendo llamarlas con diferentes números de argumentos.

### ¿Por qué este ejercicio?
- **Flexibilidad**: Funciones que funcionan con o sin argumentos
- **Conveniencia**: Valores predeterminados útiles
- **Aplicación práctica**: Muy común en Python
- **Simplicidad**: Código más simple de usar

### En el mundo real:
Cuando un sistema tiene funciones con parámetros opcionales, usa argumentos por defecto. Sin argumentos por defecto, tendrías que pasar todos los argumentos siempre.`,
      keyPoints: [
        'Argumentos por defecto se definen con =',
        'Puedes llamar la función con o sin argumento',
        'Útil para funciones flexibles',
        'Muy común en Python'
      ],
      realWorldExample: 'En un sistema de logging, log(mensaje, nivel="INFO") permite omitir el nivel si es INFO.'
    },
    theory: `# Argumentos por Defecto

## Sintaxis

\`\`\`python
def saludar(nombre="Usuario"):
    return f"Hola, {nombre}!"
\`\`\`

## Uso

\`\`\`python
saludar("Ana")  # "Hola, Ana!"
saludar()       # "Hola, Usuario!"
\`\`\`

## Ventajas

- **Flexibilidad**: Funciones más versátiles
- **Conveniencia**: Valores predeterminados
- **Simplicidad**: Menos argumentos requeridos`,
    tips: [
      'Argumentos por defecto se definen con =',
      'Puedes llamar la función con o sin argumento',
      'Útil para funciones flexibles',
      'Muy común en Python',
      'Puedes tener múltiples argumentos por defecto'
    ],
    advice: [
      'Practica definiendo funciones con argumentos por defecto',
      'Experimenta llamando funciones con y sin argumentos',
      'Aprende cuándo usar argumentos por defecto',
      'Practica en contextos de funciones flexibles'
    ]
  },
  {
    id: 5,
    title: 'Funciones con múltiples valores de retorno',
    description: 'Aprende a devolver múltiples valores desde una función usando tuplas.',
    initialCode: `# Crea una función que devuelva múltiples valores
# calcular_estadisticas(lista) debe devolver (suma, promedio, maximo)
def calcular_estadisticas(lista):
    # Completa: devuelve una tupla con (suma, promedio, maximo)
    pass

# Prueba la función
numeros = [10, 20, 30, 40, 50]
suma, promedio, maximo = calcular_estadisticas(numeros)
print(f"Suma: {suma}, Promedio: {promedio}, Máximo: {maximo}")`,
    solution: `# Crea una función que devuelva múltiples valores
# calcular_estadisticas(lista) debe devolver (suma, promedio, maximo)
def calcular_estadisticas(lista):
    # Completa: devuelve una tupla con (suma, promedio, maximo)
    suma = sum(lista)
    promedio = suma / len(lista)
    maximo = max(lista)
    return suma, promedio, maximo

# Prueba la función
numeros = [10, 20, 30, 40, 50]
suma, promedio, maximo = calcular_estadisticas(numeros)
print(f"Suma: {suma}, Promedio: {promedio}, Máximo: {maximo}")`,
    testCases: [],
    hints: [
      'Puedes devolver múltiples valores separados por comas',
      'Python los empaqueta automáticamente en una tupla',
      'Puedes desempaquetar con: suma, promedio, maximo = funcion()',
      'Útil cuando necesitas devolver varios valores relacionados'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué devolver múltiples valores?',
      content: `Devolver **múltiples valores** permite que una función calcule y devuelva varios resultados relacionados de una vez, evitando múltiples llamadas.

### ¿Por qué este ejercicio?
- **Eficiencia**: Una función calcula múltiples valores
- **Conveniencia**: Obtener resultados relacionados juntos
- **Aplicación práctica**: Muy común en Python
- **Simplicidad**: Código más claro

### En el mundo real:
Cuando un sistema calcula estadísticas de una lista, devuelve múltiples valores (suma, promedio, máximo) de una vez. Sin múltiples valores de retorno, necesitarías múltiples funciones o llamadas.`,
      keyPoints: [
        'Puedes devolver múltiples valores separados por comas',
        'Python los empaqueta en una tupla',
        'Puedes desempaquetar al llamar la función',
        'Útil para valores relacionados'
      ],
      realWorldExample: 'En un sistema de análisis, calcular_estadisticas() devuelve (suma, promedio, maximo, minimo) de una vez.'
    },
    theory: `# Múltiples Valores de Retorno

## Sintaxis

\`\`\`python
def calcular_estadisticas(lista):
    suma = sum(lista)
    promedio = suma / len(lista)
    return suma, promedio, maximo
\`\`\`

## Desempaquetado

\`\`\`python
suma, promedio, maximo = calcular_estadisticas([10, 20, 30])
\`\`\`

## Ventajas

- **Eficiencia**: Una función, múltiples resultados
- **Conveniencia**: Valores relacionados juntos
- **Simplicidad**: Código más claro`,
    tips: [
      'Puedes devolver múltiples valores separados por comas',
      'Python los empaqueta en una tupla',
      'Puedes desempaquetar al llamar la función',
      'Útil para valores relacionados',
      'Puedes devolver cualquier número de valores'
    ],
    advice: [
      'Practica devolviendo múltiples valores',
      'Experimenta desempaquetando tuplas',
      'Aprende cuándo usar múltiples valores de retorno',
      'Practica en contextos de cálculos relacionados'
    ]
  },
  {
    id: 6,
    title: 'Usar *args para argumentos variables',
    description: 'Aprende a definir funciones que aceptan un número variable de argumentos usando *args.',
    initialCode: `# Crea una función que acepte cualquier número de argumentos
# sumar_todos(*args) debe sumar todos los argumentos pasados
def sumar_todos(*args):
    # Completa: suma todos los argumentos en args
    # args es una tupla con todos los argumentos
    pass

# Prueba la función
print(sumar_todos(1, 2, 3))
print(sumar_todos(10, 20, 30, 40, 50))`,
    solution: `# Crea una función que acepte cualquier número de argumentos
# sumar_todos(*args) debe sumar todos los argumentos pasados
def sumar_todos(*args):
    # Completa: suma todos los argumentos en args
    # args es una tupla con todos los argumentos
    return sum(args)

# Prueba la función
print(sumar_todos(1, 2, 3))
print(sumar_todos(10, 20, 30, 40, 50))`,
    testCases: [],
    hints: [
      '*args permite cualquier número de argumentos',
      'args es una tupla con todos los argumentos',
      'Puedes iterar sobre args o usar sum(args)',
      'Útil para funciones flexibles'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar *args?',
      content: `**\*args** permite definir funciones que aceptan cualquier número de argumentos, haciendo las funciones más flexibles y versátiles.

### ¿Por qué este ejercicio?
- **Flexibilidad**: Funciones que aceptan cualquier número de argumentos
- **Conveniencia**: No necesitas saber cuántos argumentos habrá
- **Aplicación práctica**: Muy común en Python
- **Versatilidad**: Funciones más poderosas

### En el mundo real:
Cuando un sistema tiene funciones que procesan listas de tamaño variable, usa *args. Sin *args, tendrías que pasar una lista explícitamente.`,
      keyPoints: [
        '*args permite cualquier número de argumentos',
        'args es una tupla con todos los argumentos',
        'Puedes iterar sobre args o usar funciones como sum()',
        'Útil para funciones flexibles'
      ],
      realWorldExample: 'En un sistema de logging, log(*mensajes) permite pasar cualquier número de mensajes.'
    },
    theory: `# Usar *args para Argumentos Variables

## Sintaxis

\`\`\`python
def sumar_todos(*args):
    return sum(args)
\`\`\`

## Uso

\`\`\`python
sumar_todos(1, 2, 3)           # 6
sumar_todos(10, 20, 30, 40)    # 100
\`\`\`

## Características

- **\*args**: Recibe cualquier número de argumentos
- **Tupla**: args es una tupla con todos los argumentos
- **Flexibilidad**: Funciones más versátiles

## Ventajas

- **Flexibilidad**: Cualquier número de argumentos
- **Conveniencia**: No necesitas saber cuántos habrá
- **Versatilidad**: Funciones más poderosas`,
    tips: [
      '*args permite cualquier número de argumentos',
      'args es una tupla con todos los argumentos',
      'Puedes iterar sobre args o usar funciones como sum()',
      'Útil para funciones flexibles',
      'Puedes combinar *args con argumentos normales'
    ],
    advice: [
      'Practica definiendo funciones con *args',
      'Experimenta pasando diferentes números de argumentos',
      'Aprende cuándo usar *args',
      'Practica en contextos de funciones flexibles'
    ]
  }
];
